import React, { ReactNode } from 'react';
import classnames from 'classnames';

import styles from './flyout-item.css';

export interface FlyoutItemProps {
  /** Sets the target of the a tag */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /** Callback when clicked */
  onClick?: (e: React.MouseEvent) => void;
  /** (Legacy) Used for opening old webapp modals. Eventually these will be transitioned to React modals. */
  remote?: boolean;
  /** The content to display inside the button */
  children: ReactNode;
  /** Adds additional classes to the component. */
  className?: string;
  /** URL to redirect the user when clicked */
  href?: string;
  /** Provides a data-testid */
  'data-testid'?: string;
  /** Confirmation message, before performing an action */
  'data-confirm'?: string;
  /** Lets you change the HTTP method used to reach the endpoint */
  'data-method'?: string;
  /** Used by the Ruby On Rails framework to submit the form/link/button as an ajax request and used as a hack to trigger the rails route in a modal. */
  'data-remote'?: string;
}

/**
 * FlyoutItem component, generally rendered inside a Flyout
 *
 * @param {FlyoutItemProps} param
 *
 * @returns JSX.ELEMENT
 */
const FlyoutItem = ({
  href,
  target,
  children,
  onClick,
  className,
  remote,
  'data-testid': dataTestId,
  'data-confirm': dataConfirm,
  'data-method': dataMethod,
  'data-remote': dataRemote
}: FlyoutItemProps) => {
  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.preventDefault();
      onClick(e);
    }
  };

  // since we support two attributes to do the same thing we need to check for both
  const dataRemoteValue = remote || dataRemote === 'true' ? { 'data-remote': true } : {};

  const linkProps = {
    className: classnames([styles.basic, styles.item, className, remote && 'js-overlay-link']),
    href,
    target,
    ...dataRemoteValue,
    onClick: handleClick,
    'data-testid': dataTestId,
    'data-confirm': dataConfirm,
    'data-method': dataMethod
  };

  return href ? <a {...linkProps}>{children}</a> : <div {...linkProps}>{children}</div>;
};

export default FlyoutItem;
